import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import { IPlayerState } from 'types/common';
import { bind } from 'utils/decorators/bind';

const COMMON_STYLES = `
  width: 100%;
`;

interface ICanvasVideoRendererProps {
  state: StateManager<IPlayerState>;
  videoElement: HTMLVideoElement;
  className?: string;
  eventBus: TVideoControllerEventBus;
}

export class CanvasVideoRenderer {
  private _canvasContext: CanvasRenderingContext2D | null;
  private _canvasAnimationFrameId?: number | null;
  private _logger: TLogger;
  private _knownVideoElementStyles = '';

  private _eventsMap = {
    [EVideoElementControllerBusEvent.PLAYING]: this.startAnimationFrame,
    [EVideoElementControllerBusEvent.LOADING]: this.cancelAnimationFrame,
    [EVideoElementControllerBusEvent.REMOVE_MEDIA_STREAM]: this.cancelAnimationFrame,
  };

  private readonly _state: StateManager<IPlayerState>;
  private readonly _eventBus: TVideoControllerEventBus;

  constructor(props: ICanvasVideoRendererProps) {
    this._state = props.state;
    this._eventBus = props.eventBus;
    this._logger = Logger(this._eventBus, 'VideoElementController - CanvasVideoRenderer');

    const canvasElement = document.createElement('canvas');

    this._canvasContext = canvasElement.getContext('2d');

    if (props.className) {
      canvasElement.className = props.className;
    }

    this.init();

    this._state.setState({
      canvasElement: canvasElement,
    });

    this._applyStyles();

    this._logger.verbose('Canvas renderer has been created');
  }

  @bind
  public startAnimationFrame() {
    const { canvasElement, videoElement } = this._getElements();

    this.cancelAnimationFrame();

    if (!canvasElement || !videoElement) {
      return;
    }

    const { width, height } = canvasElement.getBoundingClientRect();
    const { videoWidth, videoHeight } = videoElement;

    const containerAspectRatio = width / height;
    const videoAspectRatio = videoWidth / videoHeight;

    if (containerAspectRatio < 1 && videoAspectRatio > 1) {
      const imageWidth = Math.max(width, videoWidth);
      const imageHeight = videoHeight * (width / videoWidth);

      const dx = width > imageWidth ? (width - imageWidth) / 2 : 0;
      const dy = (height - imageHeight) / 2;

      if (this._canvasContext) {
        const ratio = devicePixelRatio;
        canvasElement.width = imageWidth * ratio;
        canvasElement.height = height * ratio;
        this._canvasContext.scale(ratio, ratio);
      }

      this._canvasContext?.drawImage(videoElement, dx, dy, imageWidth, imageHeight);
    } else {
      const imageHeight = Math.max(height, videoHeight);
      const imageWidth = videoWidth * (height / videoHeight);

      const dy = height > imageHeight ? (height - imageHeight) / 2 : 0;
      const dx = (width - imageWidth) / 2;

      if (this._canvasContext) {
        const ratio = devicePixelRatio;
        canvasElement.width = width * ratio;
        canvasElement.height = imageHeight * ratio;
        this._canvasContext.scale(ratio, ratio);
      }

      this._canvasContext?.drawImage(videoElement, dx, dy, imageWidth, imageHeight);
    }

    if (!this._state.getState().isLoading) {
      this._canvasAnimationFrameId = requestAnimationFrame(this.startAnimationFrame);
    }
  }

  public getCanvasElement() {
    return this._getElements().canvasElement;
  }

  @bind
  public destroy() {
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });

    const { canvasElement, videoElement } = this._getElements();

    if (canvasElement && canvasElement.parentNode) {
      canvasElement.parentNode.removeChild(canvasElement);
    }

    if (videoElement) {
      videoElement.style.cssText = this._knownVideoElementStyles;
      this._knownVideoElementStyles = '';
    }

    this._state.setState({
      canvasElement: null,
    });

    this._logger.verbose('Canvas renderer was destroyed');
  }

  @bind
  public init() {
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  @bind
  public cancelAnimationFrame(): void {
    if (this._canvasAnimationFrameId) {
      cancelAnimationFrame(this._canvasAnimationFrameId);

      this._canvasAnimationFrameId = null;
    }
  }

  @bind
  private _getElements() {
    const { videoElement, canvasElement } = this._state.getState();
    return { videoElement, canvasElement };
  }

  private _applyStyles = () => {
    this._logger.debug('Apply styles for canvas and video elements');
    const { canvasElement, videoElement } = this._getElements();
    if (!canvasElement || !videoElement) {
      return;
    }

    this._knownVideoElementStyles = videoElement.style.cssText;

    canvasElement.style.cssText = COMMON_STYLES;
    videoElement.style.cssText = `
      ${COMMON_STYLES}
      opacity: 0;
      position: absolute;
      left: 0;
      top: 0;
    `;
  };
}
